import { AnchorButton, Text } from '@payaca/ambrosia';
import qs from 'qs';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router';

import { useTranslation } from '../../../i18n';

import PageBanner from '@/ui/components/pageBanner/PageBanner';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import ServicePlansCustomerRemindersListed from '@/ui/components/servicePlansCustomerRemindersListed/ServicePlansCustomerRemindersListed';
import ServicePlansSubscriptionsTable from '@/ui/components/servicePlansSubscriptionsTable/ServicePlansSubscriptionsTable';
import { useAccount } from '@/api/rest/users/useAccount';
import { ServicePlanPermissions } from '@payaca/permissions/service-plans/service-plans.permissions';
import { Link } from 'react-router-dom';
import ServicePlanDiscountCodesListed from '../../components/servicePlanDiscountCodesListed/ServicePlanDiscountCodesListed';
import ServicePlansListedPlans from '../../components/servicePlansListedPlans/ServicePlansListedPlans';
import ServicePlansServicePeriods from '../../components/servicePlansServicePeriods/ServicePlansServicePeriods';
import TabbedContentPageWrapper from '../pageWrappers/tabbedContentPageWrapper/TabbedContentPageWrapper';

enum ViewType {
  LISTED_PLANS = 'listedPlans',
  SERVICE_PERIODS = 'servicePeriods',
  DISCOUNT_CODES = 'discountCodes',
  CUSTOMER_REMINDERS = 'customerReminders',
  SUBSCRIPTIONS = 'subscriptions',
}

const ServicePlansPage = () => {
  const translate = useTranslation();
  const history = useHistory();
  const { tab } = useParams<{ tab?: string }>();
  const query = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  const account = useAccount();
  const queryString = qs.stringify(query);
  if (!tab) {
    history.replace(
      `/service-plans/listed-plans${queryString ? `?${queryString}` : ''}`
    );
  }

  const tabs = useMemo(() => {
    return [
      {
        label: ViewType.LISTED_PLANS,
        title: 'Service plans',
        urlSlug: 'listed-plans',
        content: <ServicePlansListedPlans />,
      },
      {
        label: ViewType.SUBSCRIPTIONS,
        title: 'Subscriptions',
        urlSlug: 'subscriptions',
        content: <ServicePlansSubscriptionsTable />,
      },
      {
        label: ViewType.SERVICE_PERIODS,
        title: 'Service periods',
        urlSlug: 'service-periods',
        content: <ServicePlansServicePeriods />,
      },
      {
        label: ViewType.DISCOUNT_CODES,
        title: 'Discount codes',
        urlSlug: 'discount-codes',
        content: <ServicePlanDiscountCodesListed />,
      },
      {
        label: ViewType.CUSTOMER_REMINDERS,
        title: 'Customer reminders',
        urlSlug: 'customer-reminders',
        content: <ServicePlansCustomerRemindersListed />,
      },
    ];
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has an unnecessary dependency: location.
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: location.hash
  }, [location.hash]);

  const activeTabLabel = tabs.find((x) => x.urlSlug === tab)?.label;

  const stripeBanner = useMemo(() => {
    if (!account.connectedToStripe) {
      return (
        <PermissionGuard
          renderIfHasPermissions={[ServicePlanPermissions.GET_SERVICE_PLANS]}
        >
          <PageBanner variant="warning">
            <Text as="span">
              You must be{' '}
              <AnchorButton
                as={Link}
                to="/companySettings/integrations-data/connections"
                variant="link"
                className="inline!"
              >
                connected to Stripe
              </AnchorButton>{' '}
              in order to create and sell service plans.
            </Text>
          </PageBanner>
        </PermissionGuard>
      );
    }
  }, [account.connectedToStripe]);

  return (
    <>
      <Helmet title={translate('pages.servicePlans.title')} />

      {stripeBanner}

      <TabbedContentPageWrapper
        className="service-plans-page"
        tabbedContentConfig={{
          tabs: tabs,
          activeTabLabel: activeTabLabel,
          onChangeTab: (label: string) => {
            const newTabSlug = tabs.find(
              (x) => x.label === (label as ViewType)
            )?.urlSlug;
            if (newTabSlug) {
              history.push(`/service-plans/${newTabSlug}`);
            }
          },
        }}
      />
    </>
  );
};

export default ServicePlansPage;
